import React, { useEffect, useState, useCallback } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { Button, CircularProgress, TextField, MenuItem } from '@material-ui/core';
import { Toast } from 'components/shared/Toaster';
import { statesOptions } from 'constants/constantVariables';
import { getMyClientContactInfo, updateMyClientAddressAndPhone, getPhoneTypes } from 'actions/client';
import styles from '../profile.module.scss';

const initialFormState = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip5: '',
    phone: '',
    phoneType: '',
};

const requiredFields = ['address1', 'city', 'state', 'zip5', 'phone'];

const validatePhone = (value) => {
    if (value.length > 12) return 'Phone must be no more than 12 characters';
    const digits = value.replace(/\D/g, '');
    if (digits.length < 10) return 'Phone must be at least 10 digits';
    return '';
};

const validateZip = (value) => {
    if (/\D/.test(value)) return 'Zipcode must contain only numbers';
    if (value.length !== 5) return 'Zipcode must be exactly 5 digits';
    return '';
};

const validate = (values) => {
    const errors = {};
    requiredFields.forEach((field) => {
        if (!values[field]?.trim()) {
            errors[field] = 'Required';
        }
    });
    if (values.phone?.trim() && !errors.phone) {
        const phoneError = validatePhone(values.phone);
        if (phoneError) errors.phone = phoneError;
    }
    if (values.zip5?.trim() && !errors.zip5) {
        const zipError = validateZip(values.zip5);
        if (zipError) errors.zip5 = zipError;
    }
    return errors;
};

const ClientInformationTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [clientName, setClientName] = useState('');
    const [formValues, setFormValues] = useState(initialFormState);
    const [savedValues, setSavedValues] = useState(initialFormState);
    const [phoneTypes, setPhoneTypes] = useState([]);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const isDirty = JSON.stringify(formValues) !== JSON.stringify(savedValues);

    const fetchContactInfo = useCallback(() => {
        setLoading(true);
        Promise.all([
            getMyClientContactInfo(),
            getPhoneTypes(),
        ])
            .then(([contactRes, phoneTypesRes]) => {
                const values = {
                    address1: contactRes?.address1 || '',
                    address2: contactRes?.address2 || '',
                    city: contactRes?.city || '',
                    state: contactRes?.state || '',
                    zip5: contactRes?.zip5 || '',
                    phone: contactRes?.phone || '',
                    phoneType: contactRes?.phoneType || '',
                };
                setClientName(contactRes?.name || '');
                setFormValues(values);
                setSavedValues(values);
                setPhoneTypes(phoneTypesRes || []);
                setErrors({});
                setTouched({});
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchContactInfo();
    }, [fetchContactInfo]);

    const maxLengths = {
        address1: 100,
        address2: 100,
        city: 50,
        state: 2,
        zip5: 5,
        phone: 12,
    };

    const handleChange = (field) => (e) => {
        let value = e.target.value;
        if (maxLengths[field]) value = value.slice(0, maxLengths[field]);
        const newValues = { ...formValues, [field]: value };
        setFormValues(newValues);
        if (touched[field]) {
            setErrors(validate(newValues));
        }
    };

    const handleBlur = (field) => () => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors(validate(formValues));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const allTouched = Object.keys(formValues).reduce((acc, key) => ({ ...acc, [key]: true }), {});
        setTouched(allTouched);
        const validationErrors = validate(formValues);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        setSaving(true);
        updateMyClientAddressAndPhone(formValues)
            .then(() => {
                Toast({ type: 'success', message: 'Client information updated successfully' });
                setSavedValues(formValues);
                setTouched({});
            })
            .finally(() => setSaving(false));
    };

    const fieldError = (field) => touched[field] && errors[field] ? errors[field] : '';

    if (loading) {
        return (
            <Card className="p-4 text-center">
                <CircularProgress />
            </Card>
        );
    }

    return (
        <Card>
            <div className={styles.profileContainer}>
                <div className={styles.profileHeader}>
                    <h5>Client Information</h5>
                </div>

                <p className="mb-3" style={{ fontSize: 16 }}>
                    <span style={{ color: 'rgba(0,0,0,0.59)' }}>Client Name: </span>
                    <strong>{clientName || '—'}</strong>
                </p>

                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="Address *"
                                value={formValues.address1}
                                onChange={handleChange('address1')}
                                onBlur={handleBlur('address1')}
                                error={!!fieldError('address1')}
                                helperText={fieldError('address1')}
                            />
                        </Col>
                        <Col md={6}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="Address 2"
                                value={formValues.address2}
                                onChange={handleChange('address2')}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="City *"
                                value={formValues.city}
                                onChange={handleChange('city')}
                                onBlur={handleBlur('city')}
                                error={!!fieldError('city')}
                                helperText={fieldError('city')}
                            />
                        </Col>
                        <Col md={4}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="State *" select
                                value={formValues.state}
                                onChange={handleChange('state')}
                                onBlur={handleBlur('state')}
                                error={!!fieldError('state')}
                                helperText={fieldError('state')}
                            >
                                {statesOptions.USA.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Col>
                        <Col md={4}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="Zipcode *"
                                value={formValues.zip5}
                                onChange={handleChange('zip5')}
                                onBlur={handleBlur('zip5')}
                                error={!!fieldError('zip5')}
                                helperText={fieldError('zip5')}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="Phone *"
                                value={formValues.phone}
                                onChange={handleChange('phone')}
                                onBlur={handleBlur('phone')}
                                error={!!fieldError('phone')}
                                helperText={fieldError('phone')}
                            />
                        </Col>
                        <Col md={6}>
                            <TextField
                                fullWidth margin="dense" variant="outlined"
                                label="Phone Type" select
                                value={formValues.phoneType}
                                onChange={handleChange('phoneType')}
                            >
                                {phoneTypes.map((pt) => (
                                    <MenuItem key={pt.id} value={pt.type}>
                                        {pt.type}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Col>
                    </Row>
                    <div className="text-right mt-3">
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={!isDirty || saving}
                        >
                            {saving ? <CircularProgress size={20} /> : 'Save'}
                        </Button>
                    </div>
                </form>
            </div>
        </Card>
    );
};

export default ClientInformationTab;